import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Button,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Stack, Alert, LinearProgress, InputAdornment, Paper
} from '@mui/material';
import { AccountBalance, Search, Refresh, FileDownload, People, School, BarChart } from '@mui/icons-material';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('ministry_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const fmtN = n => parseInt(n||0).toLocaleString();
const pct  = n => `${parseFloat(n||0).toFixed(1)}%`;
const fmtE = n => `E${parseFloat(n||0).toFixed(2)}`;

function LoginPage({ onLogin }) {
  const [form, setForm] = useState({ username:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.post('/platform/ministry/login', form);
      localStorage.setItem('ministry_token', r.data.token);
      onLogin(r.data.user);
    } catch (err) { setError(err.response?.data?.message || 'Invalid credentials'); }
    finally { setLoading(false); }
  };
  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#1a3a6b', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Card sx={{ maxWidth:360, width:'100%', borderRadius:3, mx:2 }}>
        <CardContent sx={{ p:4 }}>
          <Stack alignItems="center" sx={{ mb:3 }}>
            <AccountBalance sx={{ fontSize:48, color:'#1a3a6b', mb:1 }} />
            <Typography variant="h6" fontWeight={800}>Ministry Portal</Typography>
            <Typography variant="body2" color="text.secondary">Education Statistics Dashboard</Typography>
          </Stack>
          {error && <Alert severity="error" sx={{ mb:2, fontSize:12 }}>{error}</Alert>}
          <Stack spacing={2}>
            <TextField size="small" fullWidth label="Username" value={form.username} onChange={e => setForm(f => ({...f, username:e.target.value}))} />
            <TextField size="small" fullWidth label="Password" type="password" value={form.password} onChange={e => setForm(f => ({...f, password:e.target.value}))} onKeyDown={e => e.key==='Enter'&&submit()} />
            <Button variant="contained" fullWidth size="large" onClick={submit} disabled={loading} sx={{ bgcolor:'#1a3a6b', py:1.5 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default function MinistryDashboard() {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('ministry_user');
    return u ? JSON.parse(u) : null;
  });
  const [schools, setSchools] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRegion, setRegion] = useState('');
  const [view, setView] = useState('cards'); // cards | table

  const handleLogin = u => {
    localStorage.setItem('ministry_user', JSON.stringify(u));
    setUser(u);
  };
  const logout = () => { ['ministry_token','ministry_user'].forEach(k => localStorage.removeItem(k)); setUser(null); };

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get('/ministry/dashboard', { params: { region: filterRegion||undefined } });
      if (r.data.success) { setSchools(r.data.data.schools); setTotals(r.data.data.totals); }
    } catch { }
    finally { setLoading(false); }
  }, [user, filterRegion]);

  useEffect(() => { load(); }, [load]);

  if (!user) return <LoginPage onLogin={handleLogin} />;

  const regions = [...new Set(schools.map(s => s.region).filter(Boolean))].sort();
  const filtered = schools.filter(s => !search || `${s.school_name} ${s.region} ${s.school_code}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f0f4f8' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a3a6b', px:3, py:1.5, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AccountBalance sx={{ color:'white', fontSize:28 }} />
          <Box>
            <Typography color="white" fontWeight={700} fontSize={16}>{user.ministry} — Education Dashboard</Typography>
            <Typography color="rgba(255,255,255,0.65)" fontSize={11}>{user.region ? `Region: ${user.region}` : 'All Regions'}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography color="rgba(255,255,255,0.8)" fontSize={12}>{user.name}</Typography>
          <Button size="small" onClick={logout} variant="outlined" sx={{ color:'white', borderColor:'rgba(255,255,255,0.4)', fontSize:11 }}>Logout</Button>
        </Stack>
      </Box>

      <Box sx={{ p:3 }}>
        {/* Network totals */}
        {totals && (
          <Grid container spacing={2} sx={{ mb:3 }}>
            {[
              { label:'Total Schools', value:fmtN(filtered.length), color:'#1a3a6b' },
              { label:'Total Enrolled', value:fmtN(totals.enrolled), sub:`${fmtN(totals.male)}M / ${fmtN(totals.female)}F`, color:'#0f2b4a' },
              { label:'Total Teachers', value:fmtN(totals.teachers), color:'#2e7d32' },
              { label:'Student:Teacher Ratio', value:`${totals.teachers>0?Math.round(totals.enrolled/totals.teachers):0}:1`, color:'#c9952a' },
            ].map(c => (
              <Grid item xs={6} sm={3} key={c.label}>
                <Card variant="outlined" sx={{ borderRadius:2, borderLeft:`4px solid ${c.color}` }}>
                  <CardContent sx={{ pb:'12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{c.label}</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color:c.color }}>{c.value}</Typography>
                    {c.sub && <Typography fontSize={10} color="text.secondary">{c.sub}</Typography>}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb:2 }} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search school…" value={search} onChange={e => setSearch(e.target.value)}
            sx={{ minWidth:200 }} InputProps={{ startAdornment:<InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          {regions.length > 0 && (
            <TextField select size="small" label="Region" value={filterRegion} onChange={e => setRegion(e.target.value)} sx={{ minWidth:160 }}>
              <MenuItem value="">All regions</MenuItem>
              {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
          )}
          <Stack direction="row" sx={{ border:'1px solid #e0e0e0', borderRadius:1, overflow:'hidden' }}>
            {['cards','table'].map(v => <Button key={v} size="small" variant={view===v?'contained':'text'} onClick={() => setView(v)} sx={{ fontSize:11, borderRadius:0, bgcolor:view===v?'#1a3a6b':undefined }}>{v==='cards'?'Cards':'Table'}</Button>)}
          </Stack>
          <Box sx={{ flexGrow:1 }} />
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
            exportCSV('ministry_schools',
              ['School','Code','Type','Region','Enrolled','Male','Female','Teachers','Ratio','Attendance%','Fee%'],
              filtered.map(s => [s.school_name, s.school_code||'', s.school_type, s.region||'',
                s.enrolled||0, s.male||0, s.female||0, s.teachers||0,
                s.ratio||0, s.attendance_rate||0, s.fee_rate||0]))
          }>Export</Button>
        </Stack>

        {loading ? <CircularProgress /> : (
          <>
            {view === 'cards' ? (
              <Grid container spacing={2}>
                {filtered.length === 0 && <Grid item xs={12}><Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}><Typography color="text.secondary">No schools found</Typography></Paper></Grid>}
                {filtered.map(s => (
                  <Grid item xs={12} sm={6} md={4} key={s.id}>
                    <Card variant="outlined" sx={{ borderRadius:2, borderTop:`3px solid ${s.error?'#e0e0e0':'#1a3a6b'}` }}>
                      <CardContent>
                        <Typography fontWeight={700} fontSize={14} gutterBottom>{s.school_name}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mb:1 }}>
                          {s.school_code && <Chip label={s.school_code} size="small" sx={{ fontSize:9, fontFamily:'monospace' }} />}
                          <Chip label={s.school_type} size="small" sx={{ fontSize:9 }} />
                          {s.region && <Chip label={s.region} size="small" sx={{ fontSize:9 }} />}
                        </Stack>
                        {s.error ? (
                          <Typography fontSize={12} color="text.secondary">Stats unavailable</Typography>
                        ) : (
                          <>
                            <Grid container spacing={1} sx={{ mb:1 }}>
                              <Grid item xs={4}><Typography fontSize={9} color="text.secondary">ENROLLED</Typography><Typography fontWeight={700} fontSize={16}>{fmtN(s.enrolled)}</Typography><Typography fontSize={9} color="text.secondary">{s.male||0}M / {s.female||0}F</Typography></Grid>
                              <Grid item xs={4}><Typography fontSize={9} color="text.secondary">TEACHERS</Typography><Typography fontWeight={700} fontSize={16}>{fmtN(s.teachers)}</Typography><Typography fontSize={9} color="text.secondary">{s.ratio}:1</Typography></Grid>
                              <Grid item xs={4}><Typography fontSize={9} color="text.secondary">RATIO</Typography><Typography fontWeight={700} fontSize={16}>{s.ratio}:1</Typography></Grid>
                            </Grid>
                            <Box sx={{ mb:0.5 }}>
                              <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.2 }}>
                                <Typography fontSize={10} color="text.secondary">Attendance</Typography>
                                <Typography fontSize={10} fontWeight={700} color={s.attendance_rate>=85?'success.main':'warning.main'}>{pct(s.attendance_rate)}</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={Math.min(parseFloat(s.attendance_rate||0),100)} color={s.attendance_rate>=85?'success':'warning'} sx={{ height:5, borderRadius:3, mb:0.5 }} />
                              <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.2 }}>
                                <Typography fontSize={10} color="text.secondary">Fee Collection</Typography>
                                <Typography fontSize={10} fontWeight={700} color={s.fee_rate>=80?'success.main':'error.main'}>{pct(s.fee_rate)}</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={Math.min(parseFloat(s.fee_rate||0),100)} color={s.fee_rate>=80?'success':'error'} sx={{ height:5, borderRadius:3 }} />
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Card variant="outlined" sx={{ borderRadius:2 }}>
                <TableContainer sx={{ maxHeight:560 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      {['School','Code','Type','Region','Enrolled','M/F','Teachers','Ratio','Attendance','Fee Rate'].map(h => (
                        <TableCell key={h} sx={{ bgcolor:'#1a3a6b', color:'white', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                      ))}
                    </TableRow></TableHead>
                    <TableBody>
                      {filtered.map((s, i) => (
                        <TableRow key={s.id} hover sx={{ bgcolor: i%2===0?'white':'#fafafa' }}>
                          <TableCell sx={{ fontWeight:600, fontSize:12 }}>{s.school_name}</TableCell>
                          <TableCell sx={{ fontFamily:'monospace', fontSize:11 }}>{s.school_code||'—'}</TableCell>
                          <TableCell sx={{ fontSize:11 }}>{s.school_type}</TableCell>
                          <TableCell sx={{ fontSize:11 }}>{s.region||'—'}</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>{fmtN(s.enrolled)}</TableCell>
                          <TableCell sx={{ fontSize:11 }}>{s.male||0}/{s.female||0}</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>{fmtN(s.teachers)}</TableCell>
                          <TableCell>{s.ratio}:1</TableCell>
                          <TableCell><Chip label={pct(s.attendance_rate)} size="small" color={s.attendance_rate>=85?'success':'warning'} sx={{ fontSize:10, fontWeight:700 }} /></TableCell>
                          <TableCell><Chip label={pct(s.fee_rate)} size="small" color={s.fee_rate>=80?'success':'error'} sx={{ fontSize:10, fontWeight:700 }} /></TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      {totals && (
                        <TableRow sx={{ bgcolor:'#e8f0fe' }}>
                          <TableCell colSpan={4} sx={{ fontWeight:800 }}>TOTALS ({filtered.length} schools)</TableCell>
                          <TableCell sx={{ fontWeight:800 }}>{fmtN(filtered.reduce((s,r)=>s+(r.enrolled||0),0))}</TableCell>
                          <TableCell sx={{ fontWeight:700 }}>{fmtN(filtered.reduce((s,r)=>s+(r.male||0),0))}/{fmtN(filtered.reduce((s,r)=>s+(r.female||0),0))}</TableCell>
                          <TableCell sx={{ fontWeight:800 }}>{fmtN(filtered.reduce((s,r)=>s+(r.teachers||0),0))}</TableCell>
                          <TableCell colSpan={3} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
